import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
  useCallback,
} from "react";
import useFetchRunInstance from "../../../hooks/useFetchRunInstance";
import { Autocomplete, TextField } from "@mui/material";

interface IAutoCompleteRunList {
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  handelOnChangeRun: (event: ChangeEvent<any>, value: any) => void;
  currentRunId: string | number | null;
  sx?: React.CSSProperties;
  className?: string;
  isDisabled?: boolean;
  required?: boolean;
  error?: any;
  helperText?: string;
  listLabel?: string;
  placeholder?: string;
  includeRunId?: boolean; // ✅ NEW: Controls whether to include run ID in display
  lazyLoad?: boolean; // ✅ NEW: Controls whether to fetch data on mount or on dropdown open
}

/**
 * All execution groups list with auto complete dropdown functionality
 * @returns
 * Selected execution group with id and run_name
 */
const AutoCompleteRunList: React.FC<IAutoCompleteRunList> = ({
  setIsLoading,
  handelOnChangeRun,
  currentRunId,
  sx,
  className,
  isDisabled = false,
  required,
  error,
  helperText,
  listLabel,
  placeholder,
  includeRunId = false, // ✅ NEW: Default to true to maintain existing behavior
  lazyLoad = false, // ✅ NEW: Default to false for backward compatibility
}) => {
  // Track if dropdown has been opened (for lazy loading)
  const [hasOpened, setHasOpened] = useState(false);

  const [runInstanceData, fetchRunInstance, isFetching] = useFetchRunInstance({
    setIsLoading,
    fetchOnMount: !lazyLoad,
  });

  // Handle dropdown open - trigger fetch if lazy loading
  const handleOpen = useCallback(() => {
    if (lazyLoad && !hasOpened) {
      setHasOpened(true);
      fetchRunInstance();
    }
  }, [lazyLoad, hasOpened, fetchRunInstance]);

  // When lazy-loading with a preselected id (e.g., restored from URL),
  // fetch immediately so the selected option can be resolved on first render.
  useEffect(() => {
    if (lazyLoad && !hasOpened && currentRunId) {
      setHasOpened(true);
      fetchRunInstance();
    }
  }, [lazyLoad, hasOpened, currentRunId, fetchRunInstance]);

  // Show loading spinner while fetching data
  const isLoading = lazyLoad && (isFetching || !hasOpened);

  return (
    <Autocomplete
      sx={sx}
      defaultValue={
        currentRunId
          ? (runInstanceData || []).find(
              (option: any) => option.id === currentRunId,
            )
          : null
      }
      options={runInstanceData || []}
      value={
        (runInstanceData || []).find(
          (option: any) => option.id === currentRunId,
        ) || null
      }
      getOptionLabel={(option: any) => {
        return includeRunId
          ? `(${option.id}) ${option.run_name}` || ""
          : option.run_name || "";
      }}
      onOpen={handleOpen}
      loading={isLoading}
      renderInput={(params) => (
        <TextField
          style={{ color: "#000000" }}
          {...params}
          label={
            <span>
              {listLabel ? listLabel : "Execution Group"}
              {required ? <span className="required-asterisk">*</span> : ""}
            </span>
          }
          // required={required}
          placeholder={placeholder ? placeholder : "Select..."}
          InputLabelProps={{
            shrink: true,
          }}
          error={!!error}
          helperText={helperText || ""}
        />
      )}
      className={className}
      loadingText="Loading..."
      onChange={(event, value) => handelOnChangeRun(event, value)}
      disabled={Boolean(isDisabled)}
    />
  );
};

export default AutoCompleteRunList;
